import { useEffect, useState } from "react";
import Feedback from "../feedback/feedback-container";
import { FormattedMessage, useIntl } from "react-intl";
import Modal from "../modals/modal";
import "./session-expiration-watcher.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";
import { getClientId, getUserUIBase } from "../../api/ApiImplementation";
import { asDukeLocale } from "../localize/localize-view";

//<editor-fold desc="Props">
const PromptRenewalDelta = 300000;
const BlockUIDelta = 1000;

enum WatcherState {
  OK,
  PROMPT,
  BLOCK,
}

export interface SessionExpirationWatcherProps {
  /**
   * Time in millis that the current authentication will remain valid
   */
  authenticationIsValidUntil: number;
  externalLogout?: boolean;
}
export default function SessionExpirationWatcher(
  props: SessionExpirationWatcherProps
) {
  const { authenticationIsValidUntil, externalLogout } = props;
  const [expiresIn, setExpiresIn] = useState<number | undefined>(undefined);
  const [showPrompt, setShowPrompt] = useState<number>(WatcherState.OK);
  const [promptDismissed, setPromptDismissed] = useState<boolean>(false);

  const intl = useIntl();
  /* eslint-disable no-template-curly-in-string */
  /* tslint:disable:no-template-curly-in-string */
  const idp_base = getUserUIBase().replace(
    "${locale}",
    intl.locale !== intl.defaultLocale ? "/" + asDukeLocale(intl.locale) : ""
  );
  /* tslint:enable:no-template-curly-in-string */
  /* eslint-enable no-template-curly-in-string */
  // Handles the state
  useEffect(() => {
    let timer: any;
    let expirationTimer: any;
    if (externalLogout === true) {
      setShowPrompt(WatcherState.BLOCK);
    } else {
      const now = new Date().getTime();
      const timeToPrompt =
        authenticationIsValidUntil - now - PromptRenewalDelta;
      const timeToBlock = authenticationIsValidUntil - now - BlockUIDelta;
      let newState;
      if (timeToPrompt <= 0) {
        if (timeToBlock <= 0) {
          newState = WatcherState.BLOCK;
          // setShowPrompt(WatcherState.BLOCK);
        } else {
          newState = WatcherState.PROMPT;
          // setShowPrompt(WatcherState.PROMPT);
          timer = setTimeout(() => {
            setShowPrompt(WatcherState.BLOCK);
          }, timeToBlock);
        }
      } else {
        if (showPrompt) {
          newState = WatcherState.OK;
          // setShowPrompt(WatcherState.OK);
        }
        timer = setTimeout(() => {
          setShowPrompt(WatcherState.PROMPT);
        }, timeToPrompt);
      }

      // takes care of updating the countdown number
      if (
        (newState && newState === WatcherState.PROMPT) ||
        showPrompt === WatcherState.PROMPT
      ) {
        expirationTimer = setInterval(() => {
          setExpiresIn(
            Math.round(
              (authenticationIsValidUntil - new Date().getTime()) / 1000
            )
          );
        }, 1000);
      } else {
        setExpiresIn(undefined);
      }
      if (newState) {
        setShowPrompt(newState);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (expirationTimer) {
        clearInterval(expirationTimer);
      }
    };
  }, [authenticationIsValidUntil, showPrompt, externalLogout]);
  return (
    <>
      {showPrompt === WatcherState.PROMPT && expiresIn !== undefined && (
        <>
          {promptDismissed && (
            <span
              className={
                "session-expiration-prompt-icon alert alert-danger py-1 px-2"
              }
            >
              <a
                className="alert-link"
                href="#dismiss"
                role={"button"}
                onClick={(e) => {
                  e.preventDefault();
                  setPromptDismissed(!promptDismissed);
                }}
              >
                <FontAwesomeIcon icon="user-slash" />
                {expiresIn <= 60 ? (
                  <strong className={"ml-2"}>
                    {intl.formatNumber(expiresIn, {
                      style: "unit",
                      unit: "second",
                      unitDisplay: "narrow",
                    })}
                  </strong>
                ) : (
                  <strong className={"ml-2"}>
                    {intl.formatNumber(Math.round(expiresIn / 60), {
                      style: "unit",
                      unit: "minute",
                      unitDisplay: "short",
                    })}
                  </strong>
                )}
              </a>
            </span>
          )}
          <Feedback
            id="sessionExpirationPrompt"
            type={"danger"}
            show={!promptDismissed}
            onClose={() => setPromptDismissed(true)}
            data-test-session-will-expire
          >
            <h4 className={"alert-title"}>
              <FormattedMessage
                defaultMessage="Your session will expire {relativeTime}."
                description="copy to be shown when the session is about to expire. 'relativeTime' = numeric relative time value with full unit type identifier (only seconds or minutes are used)"
                values={{
                  relativeTime:
                    expiresIn && expiresIn <= 60
                      ? intl.formatRelativeTime(expiresIn, "second")
                      : intl.formatRelativeTime(
                          Math.round(expiresIn / 60),
                          "minute"
                        ),
                }}
              />
            </h4>
            <p>
              <FormattedMessage
                defaultMessage="If you wish to continue, finish up what you're working on and click refresh."
                description="Info copy to be shown when the session is about to expire. The intent is to give the user a heads up that they still have some time to complete what ever they are doing before their session expires."
              />
            </p>
            <div className={"float-right"}>
              <Button
                type={"button"}
                variant={"danger"}
                onClick={() => window.location.reload()}
              >
                <FormattedMessage
                  defaultMessage="Refresh"
                  description={"refresh session button label"}
                />
              </Button>
            </div>
          </Feedback>
        </>
      )}
      {showPrompt === WatcherState.BLOCK && !externalLogout && (
        <Modal
          id="sessionExpirationBlock"
          isReady={true}
          data-test-session-expired
          closable={false}
          onClose={() => {}}
          title={
            <FormattedMessage
              defaultMessage="Your session has expired"
              description="modal heading to be shown when the session has expired"
            />
          }
          show={true}
          primaryButton={{
            label: (
              <FormattedMessage
                defaultMessage="Refresh"
                description={"Refresh session button label"}
              />
            ),
          }}
          onPrimaryAction={() => window.location.reload()}
          secondaryButton={{
            label: (
              <FormattedMessage
                defaultMessage="Logout"
                description={"Logout button label"}
              />
            ),
          }}
          onSecondaryAction={() => {
            window.location.href =
              idp_base +
              "/oauth20/signout?client_id=" +
              encodeURIComponent(getClientId());
          }}
        >
          <p>
            <FormattedMessage
              defaultMessage="Your session has expired. Please refresh or log out."
              description="message to be shown when the session has expired. Refresh is referring to the refresh session button, not refreshing the page."
            />
          </p>
        </Modal>
      )}
      {showPrompt === WatcherState.BLOCK && externalLogout && (
        <Modal
          id="ExternalLogout"
          isReady={true}
          data-test-external-logout
          closable={false}
          onClose={() => {}}
          title={
            <FormattedMessage
              defaultMessage="You have been logged out"
              description="modal heading to be shown when the user has been logged out in another tab or window"
            />
          }
          show={true}
          primaryButton={{
            label: (
              <FormattedMessage
                defaultMessage="Continue"
                description={"Continue button label"}
              />
            ),
          }}
          onPrimaryAction={() => window.location.reload()}
        >
          <p>
            <FormattedMessage
              defaultMessage="Your session has been terminated in another browser window or tab."
              description="info copy 1 to be shown when the user has been logged out in another tab or window."
            />
          </p>
          <p>
            <FormattedMessage
              defaultMessage="If you wish to continue you'll need to login again."
              description="info copy 2 to be shown when the user has been logged out in another tab or window."
            />
          </p>
        </Modal>
      )}
    </>
  );
}
